/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {memo} from 'react';
import {makeStyles} from '@material-ui/core';
import isEmpty from 'lodash.isempty';
import {GenericObject, SensorType} from '@ion/core/src/types/entities';
import {themedClassName} from '@ion/dls/src/theme/helpers';
import {dlsLayout} from '@ion/dls/src/theme/theme';
import {isCurrentTheme} from '@ion/dls/src/theme/useThemeContext';
import {DLSTheme} from '@ion/dls/src/types/types';
import NoSensorData from './NoSensorData';
import {SensorAccordion} from './SensorAccordion';
import TiltSensorCrosshair from './TiltSensorCrosshair';
import TiltSensorEventStepper from './TiltSensorEventStepper';
import TiltSensorGraphic from './TiltSensorGraphic';
import TiltSensorStatus from './TiltSensorStatus';

export interface TiltSensorAccordionProps {
  data?: GenericObject,
  loading?: boolean,
  title?: string,
  noDataLabel?: string,
  // true only for the Asset Details Drawer's own SC sensor graphs - passed
  // through to SensorAccordion to gate its current theme section card ...
  isAssetDetailsCard?: boolean,
};

const useStyles = makeStyles((theme: DLSTheme) => {
  const padding = dlsLayout('gutters', 'mobile');
  return {
    content : {
      paddingLeft  : padding,
      paddingRight : padding + 10,
      paddingTop   : 0
    },
    ionTagCardListContentWrapper : {
      marginTop      : 12,
      width          : '100%',
      display        : 'flex',
      justifyContent : 'space-between',
      alignItems     : 'center',
      height         : 'auto'
    },
    tiltGraphic : {
      width          : '100%',
      display        : 'flex',
      flexDirection  : 'column',
      justifyContent : 'center',
      alignItems     : 'center',
      padding        : '12px',
      position       : 'relative'
    },
    noData : {
      padding : '0 15px'
    },
    // current theme drops the legacy top margin (the shell's own 16px flex
    // gap replaces it) and side padding ...
    currentContentWrapper : {
      marginTop : 0
    },
    currentNoData : {
      padding : 0
    }
  };
});

const TiltSensorAccordion = ({
  data,
  loading,
  title,
  isAssetDetailsCard
}: TiltSensorAccordionProps) => {
  const classes         = useStyles();
  const isCurrent       = isCurrentTheme();
  const tiltData        = data?.data?.slice(0, 4);
  const threshold       = tiltData?.[0]?.settings?.maxTip;
  const currentValue    = tiltData?.[0]?.value;
  const isOverThreshold = currentValue > threshold;

  return (
    <SensorAccordion title={title}
      isLoading={loading}
      isAssetDetailsCard={isAssetDetailsCard}>
      {!isEmpty(data?.data) ? (
        <>
          <TiltSensorStatus data={data} isOverThreshold={isOverThreshold}/>
          <div className={themedClassName(
            classes.ionTagCardListContentWrapper,
            classes.currentContentWrapper,
            isCurrent
          )}>
            <TiltSensorEventStepper tiltData={tiltData} isOverThreshold={isOverThreshold}/>
            <div className={classes.tiltGraphic}>
              <TiltSensorCrosshair />
              <TiltSensorGraphic isOverThreshold={isOverThreshold} />
            </div>
          </div>
        </>
      ) : (
        <div className={themedClassName(classes.noData, classes.currentNoData, isCurrent)}>
          {/* without data there's no id, so the current theme names the type ... */}
          <NoSensorData sensorType={isCurrent ? SensorType.Tilt : data?.id} />
        </div>
      )}
    </SensorAccordion>
  );
};

export default memo(TiltSensorAccordion);
